import React, { useState } from "react";
import { Button } from "../common";
import { Alert } from "flowbite-react";
import { VendorT } from "../../types";
import { formatNumber } from "../../utils";
import PhoneInput from "react-phone-number-input";
import { InputComponent } from "../core/Forms";
import { clearFieldError } from "../../utils/handleErrors";
import { validateVendorForm } from "../../modules/vendor/vendor";
import LocationSelector from "../common/Address";
import { IVendor } from "../../@types";
import PhoneInputComponent from "../common/PhoneInputComponent";
import { CloseIcon } from "../../assets/icons";
import { useVendorStore } from "../../state/useVendorStore";

interface CreateFormProps {
  show: boolean;
  onSubmit: any;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClose?: () => void;
  buttontitle: string;
  isLoading: boolean;
  vendor?: VendorT;
  errorMessage?: string;
  onSelectVendor?: (selectedVendor: IVendor) => void;
}

const CreateVendorForm: React.FC<CreateFormProps> = (props) => {
  const { vendor, onSubmit, isLoading, onClose, onSelectVendor } = props;
  React.useState("");
  const [, setErrorMessage] = React.useState("");
  const [, setIsOpen] = React.useState(props.show);
  const [value, setValue] = useState("");
  const [state, setState] = React.useState(vendor?.state || "");
  const [city, setCity] = React.useState(vendor?.city || "");
  const [placeholder] = useState("+234 900 000 0000");
  const [name, setName] = React.useState(vendor?.name || "");
  const [lastName, setLastName] = React.useState(vendor?.lastName || "");
  const [phone, setPhone] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [showMoreFields, setShowMoreFields] = useState(false); // State for toggling
  const addVendor = useVendorStore((state) => state.createVendor); // Get the function from Zustand store
  const [validationResults, setValidationResults] = useState({
    name: "",
    lastName: "",
    businessName: "",
  });

  function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "name") {
      setName(capitalizeFirstLetter(value));
    } else if (name === "lastName") {
      setLastName(capitalizeFirstLetter(value));
    } else if (name === "address") {
      setAddress(value);
    } else if (name === "businessName") {
      setBusinessName(value);
    }

    clearFieldError(name, validationResults, setValidationResults);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { hasErrors, errors } = validateVendorForm({
      name,
      lastName,
      businessName,
    });

    setValidationResults(errors);

    if (!hasErrors) {
      const newVendor = {
        id: Date.now().toString(),
        name,
        lastName,
        phone,
        address,
        state,
        city,
        businessName,
      };

      try {
        await onSubmit(newVendor);
        // If onSelectVendor is passed, call it with the new vendor
        if (onSelectVendor) {
          onSelectVendor(newVendor);
        }
      } catch (error) {
        props.errorMessage && setErrorMessage(props.errorMessage);
      }
    }
  };

  const handlePhoneNumberChange = (value: string) => {
    const trimmedValue = value.trim();

    if (trimmedValue.length === 14) {
      const lastChar = trimmedValue.charAt(trimmedValue.length - 1);
      if (lastChar === " ") {
        const formattedNumber = `${trimmedValue.substring(
          1,
          trimmedValue.length - 1,
        )}`;
        setValue(formattedNumber);
        setPhone(formattedNumber);
      } else {
        setValue(trimmedValue);
        setPhone(trimmedValue.substring(4));
      }
    } else {
      setValue(trimmedValue);
      setPhone(trimmedValue.substring(4));
    }
  };

  React.useEffect(() => {
    setIsOpen(props.show);
  }, []);

  const handleLocationChange = (
    selectedState: string,
    cities: string | any[],
  ) => {
    setState(selectedState);
    setCity(cities.length > 0 ? cities[0] : "");
  };

  const handlePhoneNumberKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.currentTarget.value.length >= 17) {
      event.preventDefault();
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowAddressFields(e.target.checked);
  };

  return (
    <div className="flex flex-col w-full justify-start md:p-4">
      <form onSubmit={handleSubmit}>
        {props.errorMessage && (
          <Alert className="text-center" color="failure">
            {props.errorMessage}
          </Alert>
        )}

        {/* Form Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[20px] hidden md:block font-medium lg:text-[24px]">
            Add New Vendor
          </h3>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-8">
          <div>
            <label className="font-normal text-[#000000] text-[14px]">
              Business Name <span className="text-red-500">*</span>
            </label>
            <InputComponent
              name="businessName"
              type="text"
              className="block w-full border border-gray-300 rounded-md shadow-sm"
              placeholder="Business name"
              handleChange={handleInputChange}
              error={validationResults.businessName}
            />
          </div>

          <div>
            <label className="font-normal text-[#000000] text-[14px]">
              Email
            </label>
            <InputComponent
              name="email"
              type="email"
              className="block w-full border border-gray-300 rounded-md shadow-sm"
              placeholder="Email"
              handleChange={handleInputChange}
            />
          </div>

          <div>
            <label className="font-normal text-[#000000] text-[14px]">
              First Name  <span className="text-red-500">*</span>
            </label>
            <InputComponent
              name="name"
              type="text"
              className="block w-full border border-gray-300 rounded-md shadow-sm"
              placeholder="First name"
              value={name}
              handleChange={handleInputChange}
              error={validationResults.name}
            />
          </div>

          <div>
            <label className="font-normal text-[#000000] text-[14px]">
              Last Name  <span className="text-red-500">*</span>
            </label>
            <InputComponent
              name="lastName"
              type="text"
              className="block w-full border border-gray-300 rounded-md shadow-sm"
              placeholder="Last name"
              value={lastName}
              handleChange={handleInputChange}
              error={validationResults.lastName}
            />
          </div>

          <div>
            <label className="font-normal text-[#000000] text-[14px]">
              Phone Number
            </label>
            <PhoneInputComponent
              onPhoneNumberChange={handlePhoneNumberChange}
              initialPhoneNumber={phone}
              containerClass="customer"
              inputClass="customer"
            />
          </div>

          <div>
            <label className="font-normal text-[#000000] text-[14px]">
              Address
            </label>
            <InputComponent
              name="address"
              type="text"
              className="block w-full border border-gray-300 rounded-md shadow-sm"
              placeholder="Address"
              value={address}
              handleChange={handleInputChange}
            />
          </div>
        </div>

        {/* Location Selector */}
        <div className="mt-4">
          <LocationSelector
            onLocationChange={handleLocationChange}
            onError={(error: any) => {}}
            selectedState={state}
            selectedCity={city}
            display="w-full"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex w-full justify-center gap-4 mt-6">
          <Button
            outline={true}
            variant="secondary"
            disabled={false}
            text="Cancel"
            onClick={onClose}
            size="full"
          />
          <Button
            isLoading={isLoading}
            text="Save"
            variant="secondary"
            onClick={onSubmit}
            size="full"
          />
        </div>
      </form>
    </div>
  );
};

export default CreateVendorForm;
